// pages/instructor/components/InstructorNavbar.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Button,
  Avatar,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import BarChartIcon from "@mui/icons-material/BarChart";
import Logo from "../../../assets/Header/techlogo2.png";

const navLinks = [
  {
    text: "Dashboard",
    path: "/addcourse",
    icon: <DashboardIcon fontSize="small" />,
  },
  {
    text: "My Courses",
    path: "/",
    icon: <VideoLibraryIcon fontSize="small" />,
  },
  {
    text: "Students",
    path: "/",
    icon: <PeopleIcon fontSize="small" />,
  },
  {
    text: "Analytics",
    path: "/",
    icon: <BarChartIcon fontSize="small" />,
  },
];

const InstructorNavbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("/instructor/dashboard");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        setUserInfo({
          name: data.firstname || "Instructor",
          email: data.email || "",
          avatar: data.avatar || "",
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/");
    window.location.reload();
  };

  const handleMenuItemClick = (action) => {
    handleCloseUserMenu();
    switch (action) {
      case "Dashboard":
        navigate("/");
        break;
      case "My Courses":
        navigate("/");
        break;
      case "Students":
        navigate("/");
        break;
      case "Earnings":
        navigate("/");
        break;
      case "Settings":
        navigate("/");
        break;
      case "Logout":
        handleLogout();
        break;
      default:
        break;
    }
  };

  const userMenuItems = [
    { label: "Dashboard", icon: <DashboardIcon /> },
    { label: "My Courses", icon: <VideoLibraryIcon /> },
    { label: "Students", icon: <PeopleIcon /> },
    { label: "Earnings", icon: <AttachMoneyIcon /> },
    { label: "Settings", icon: <SettingsIcon /> },
    { divider: true },
    { label: "Logout", icon: <LogoutIcon />, danger: true },
  ];

  return (
    <AppBar
      position="fixed"
      elevation={scrolled ? 4 : 0}
      sx={{
        backgroundColor: scrolled ? "#ffffffcc" : "#fff",
        backdropFilter: scrolled ? "blur(20px)" : "blur(10px)",
        borderBottom: scrolled
          ? "1px solid rgba(255, 107, 107, 0.1)"
          : "1px solid rgba(255, 107, 107, 0.05)",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: scrolled ? "0 4px 30px rgba(255, 107, 107, 0.08)" : "none",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: { xs: 64, md: 80 } }}>
          {/* Logo - Desktop */}
          <Box
            component={NavLink}
            to="/instructor/dashboard"
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              textDecoration: "none",
              flexShrink: 0,
              gap: 1,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -4,
                left: 0,
                width: "100%",
                height: 2,
                background: "linear-gradient(90deg, #FF6B6B, #FF8E53)",
                transform: "scaleX(0)",
                transformOrigin: "right",
                transition: "transform 0.3s ease",
              },
              "&:hover::after": {
                transform: "scaleX(1)",
                transformOrigin: "left",
              },
            }}
          >
            <Box
              component="img"
              src={Logo}
              alt="Wavepalm Techub"
              sx={{
                height: 48,
                width: "auto",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
            <Chip
              label="Instructor"
              size="small"
              sx={{
                backgroundColor: "rgba(255, 107, 107, 0.12)",
                color: "#FF6B6B",
                fontWeight: 700,
                fontSize: "0.6rem",
                height: 20,
                border: "1px solid rgba(255, 107, 107, 0.2)",
                ml: 1,
              }}
            />
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ display: { xs: "flex", md: "none" }, flex: 1 }}>
            <IconButton
              size="large"
              aria-label="menu"
              onClick={handleOpenNavMenu}
              sx={{
                color: "#FF6B6B",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "rotate(90deg)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Logo - Mobile */}
          <Box
            component={NavLink}
            to="/instructor/dashboard"
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              textDecoration: "none",
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={Logo}
              alt="Wavepalm Techub"
              sx={{
                height: 35,
                width: "auto",
              }}
            />
          </Box>

          {/* Mobile Menu Drawer */}
          <Menu
            anchorEl={anchorElNav}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            PaperProps={{
              sx: {
                width: "100%",
                maxWidth: 400,
                height: "100vh",
                backgroundColor: "#F7F8F2",
                borderRadius: 0,
                border: "none",
                p: 3,
                position: "relative",
              },
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseNavMenu}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "rgba(255, 107, 107, 0.5)",
                "&:hover": {
                  color: "#FF6B6B",
                  backgroundColor: "rgba(255, 107, 107, 0.05)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Mobile Logo */}
            <Box sx={{ mb: 4, mt: 2 }}>
              <Box
                component="img"
                src={Logo}
                alt="Wavepalm Techub"
                sx={{
                  height: 40,
                  width: "auto",
                }}
              />
            </Box>

            {/* Mobile Nav Links */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {navLinks.map((link) => (
                <MenuItem
                  key={link.text}
                  onClick={handleCloseNavMenu}
                  component={NavLink}
                  to={link.path}
                  sx={{
                    py: 1.5,
                    px: 3,
                    color: activeLink === link.path ? "#FF6B6B" : "#333333",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    backgroundColor:
                      activeLink === link.path
                        ? "rgba(255, 107, 107, 0.08)"
                        : "transparent",
                    border:
                      activeLink === link.path
                        ? "1px solid rgba(255, 107, 107, 0.2)"
                        : "1px solid transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255, 107, 107, 0.05)",
                      transform: "translateX(8px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ color: "#FF6B6B" }}>{link.icon}</Box>
                    <Typography fontWeight={500}>{link.text}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Box>

            <Box
              sx={{
                mt: "auto",
                pt: 3,
                borderTop: "1px solid rgba(255, 107, 107, 0.08)",
              }}
            >
              <Button
                variant="contained"
                fullWidth
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: "#FF6B6B",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#E55555",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Logout
              </Button>
            </Box>
          </Menu>

          {/* Desktop Navigation */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            {navLinks.map((link) => (
              <Button
                key={link.text}
                component={NavLink}
                to={link.path}
                onClick={handleCloseNavMenu}
                startIcon={link.icon}
                sx={{
                  color:
                    activeLink === link.path
                      ? "#FF6B6B"
                      : "rgba(51, 51, 51, 0.7)",
                  px: 2.5,
                  py: 1,
                  fontWeight: activeLink === link.path ? 600 : 500,
                  borderRadius: 2,
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    bottom: 4,
                    left: "50%",
                    transform: "translateX(-50%) scaleX(0)",
                    width: "70%",
                    height: 2,
                    background: "linear-gradient(90deg, #FF6B6B, #FF8E53)",
                    borderRadius: 2,
                    transition: "transform 0.3s ease",
                  },
                  "&:hover": {
                    color: "#FF6B6B",
                    backgroundColor: "rgba(255, 107, 107, 0.05)",
                  },
                  "&:hover::before": {
                    transform: "translateX(-50%) scaleX(1)",
                  },
                  "&.active": {
                    color: "#FF6B6B",
                    backgroundColor: "rgba(255, 107, 107, 0.08)",
                  },
                  "&.active::before": {
                    transform: "translateX(-50%) scaleX(1)",
                  },
                }}
                className={activeLink === link.path ? "active" : ""}
              >
                {link.text}
              </Button>
            ))}
          </Box>

          {/* User Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 2 },
            }}
          >
            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton sx={{ color: "rgba(51, 51, 51, 0.5)" }}>
                <Badge badgeContent={5} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Tooltip title="Account settings">
              <Box
                onClick={handleOpenUserMenu}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  p: 0.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(255, 107, 107, 0.05)",
                  },
                }}
              >
                <Avatar
                  alt={userInfo?.name}
                  src={userInfo?.avatar || ""}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#FF6B6B",
                  }}
                >
                  {userInfo?.name?.charAt(0) || "I"}
                </Avatar>
                {!isMobile && (
                  <Typography variant="body2" fontWeight={500} color="#333333">
                    {userInfo?.name}
                  </Typography>
                )}
                <ExpandMoreIcon
                  sx={{ color: "rgba(51, 51, 51, 0.5)", fontSize: 20 }}
                />
              </Box>
            </Tooltip>

            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              onClick={handleCloseUserMenu}
              sx={{
                mt: "45px",
                "& .MuiPaper-root": {
                  borderRadius: 2,
                  minWidth: 220,
                  backgroundColor: "#F7F8F2",
                  boxShadow: "0 4px 20px rgba(255, 107, 107, 0.08)",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "top" }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    alt={userInfo?.name}
                    src={userInfo?.avatar || ""}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: "#FF6B6B",
                    }}
                  >
                    {userInfo?.name?.charAt(0) || "I"}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="#1a1a1a"
                    >
                      {userInfo?.name}
                    </Typography>
                    <Typography variant="caption" color="rgba(26, 26, 26, 0.5)">
                      {userInfo?.email}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Instructor"
                  size="small"
                  sx={{
                    mt: 1,
                    backgroundColor: "rgba(255, 107, 107, 0.1)",
                    color: "#FF6B6B",
                    fontWeight: 600,
                    border: "1px solid rgba(255, 107, 107, 0.15)",
                  }}
                />
              </Box>
              <Divider sx={{ backgroundColor: "rgba(255, 107, 107, 0.08)" }} />

              {userMenuItems.map((item, index) => {
                if (item.divider) {
                  return (
                    <Divider
                      key={`divider-${index}`}
                      sx={{ backgroundColor: "rgba(255, 107, 107, 0.08)" }}
                    />
                  );
                }
                return (
                  <MenuItem
                    key={item.label}
                    onClick={() => handleMenuItemClick(item.label)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      gap: 1.5,
                      color: item.danger ? "#FF6B6B" : "#1a1a1a",
                      "&:hover": {
                        backgroundColor: item.danger
                          ? "rgba(255, 107, 107, 0.08)"
                          : "rgba(255, 107, 107, 0.05)",
                      },
                    }}
                  >
                    {item.icon}
                    <Typography variant="body2">{item.label}</Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default InstructorNavbar;
